import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import { APIBackend } from 'libs/e2api/APIBackend';
import { Client } from 'libs/e2api/Client';
import { decodeClaims, getIdByScope } from './E2ClientProvider.helpers';
import { E2ClientContextI, E2ClientProviderProps, Scope } from './E2ClientProvider.types';

export const E2ClientContext = createContext<E2ClientContextI>({
  client: null,
  infographId: null, // e2 infograph id
  isReadonly: false,
});

/**
 * This component provides the e2 client and infograph id to the context.
 * It loads the token using react-router loader. The token is used to create the e2api client instance.
 * The e2 infograph id is extracted from the token or the route params.
 * if it is the standalone mode, it uses the infograph ID that is in the route params.
 *
 * @param {ReactNode} children
 * @returns
 */
const E2ClientProvider: React.FC<E2ClientProviderProps> = ({ children }: E2ClientProviderProps) => {
  const token = useLoaderData() as string;
  const backend = useRef<APIBackend>(new APIBackend({ authToken: token }));
  const client = useRef<Client>(new Client(backend.current));
  const { id: infographId, privateLinkId } = useParams();

  // Returns E2 infograph id from jwt permission or current infograph ID in the route params
  const getInfographId = useCallback(() => {
    try {
      if (!token) return;

      const claims = decodeClaims(token);
      const value = getIdByScope(Scope.Infograph, claims.perm);

      // '*' means standalone. Therefore, it returns the infograph ID in the route params.
      const result = value === '*' ? infographId ?? privateLinkId : value;

      return result;
    } catch (err: unknown) {
      if (err instanceof Error) {
        throw new Error(err.message);
      }
    }
  }, [token, infographId, privateLinkId]);

  // TODO: Remove this when we have proper permission management in the E2 Backend
  // Check this infograph scope has a readonly permission
  const isReadonly = useMemo(() => {
    if (!token) return false;

    const claims = decodeClaims(token);

    const targetKey = Object.keys(claims.perm).find((key) => {
      return key.split(':')[1] === Scope.Infograph;
    });

    return !!(targetKey?.split(':')?.[0] === 'r');
  }, [token]);

  const value = useMemo(
    () => ({
      client: client.current,
      infographId: getInfographId()!,
      isReadonly,
    }),
    [isReadonly, getInfographId],
  );

  return <E2ClientContext.Provider value={value}>{children}</E2ClientContext.Provider>;
};

/**
 * This hooks returns the e2 client and infograph id from the context.
 *
 * client: E2 backend client instance
 * infographId: E2 infograph id
 *
 * @returns {E2ClientContextI} E2ClientContext
 * @example
 * const { client, infographId } = useE2Client();
 */
export const useE2Client = () => {
  const e2Client = useContext(E2ClientContext);
  return e2Client;
};

export default E2ClientProvider;
